// Decoración de Día de Muertos (pedido del dueño, 2026-11-01: "quita lo de
// halloween y pon lo de día de muertos"). Mismas reglas que tenía la de
// Halloween: ilustraciones SVG propias (nada de emojis), colores de papel
// picado (rosa mexicano, naranja cempasúchil, morado, verde, turquesa).
//
// - Se muestra solo del 28 de octubre al 2 de noviembre y desaparece sola.
// - Nada de esto recibe clics (pointer-events-none) ni lo leen los lectores
//   de pantalla (aria-hidden), así que no estorba para comprar.
// - Con "reducir movimiento" activado en el teléfono, todo se queda quieto.
// - No usa el rojo de marca (DESIGN.md: el rojo es solo para acciones).

export function esTemporadaMuertos(fecha = new Date()) {
  // Desactivado por el dueño (2026-11-03): ya no se muestra el adorno de
  // Día de Muertos. Dejar en false para que no vuelva solo; para reactivarlo
  // el año que viene, restaurar la regla por fecha.
  return false;
}

const C = {
  noche: "#1a0b2e",
  noche2: "#4a1250",
  oscuro: "#1e1b2e",
  rosa: "#db2777",
  naranja: "#f97316",
  naranjaOsc: "#ea580c",
  ambar: "#f59e0b",
  amarillo: "#fbbf24",
  amarilloClaro: "#fde68a",
  morado: "#7c3aed",
  verde: "#16a34a",
  turquesa: "#0891b2",
  hueso: "#fffbeb",
  // cempasúchil dorado (pedido del dueño 2026-11-01: "más dorado")
  oro1: "#d97706",
  oro2: "#f59e0b",
  oro3: "#fbbf24",
  oro4: "#fcd34d",
  oro5: "#fef08a",
  oroBorde: "#b45309",
};

const PAPEL = [C.rosa, C.naranja, C.morado, C.verde, C.amarillo, C.turquesa];

/* ------------------------------------------------------------------ */
/* Ilustraciones                                                       */
/* ------------------------------------------------------------------ */

const circ = (cx, cy, r) => `M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0 Z`;

// Banderita de papel picado: el borde de abajo en zigzag y los recortes son
// huecos de verdad (evenodd), así se ve lo que hay detrás.
const BANDERA_D = (() => {
  let d = "M0 0 H30 V32";
  for (let i = 1; i <= 10; i += 1) d += ` L${30 - i * 3} ${i % 2 ? 36 : 32}`;
  d += " Z";
  d += " " + circ(15, 15, 3.5);
  d += " M15 4.5 L17.5 7 L15 9.5 L12.5 7 Z M23 12.5 L25.5 15 L23 17.5 L20.5 15 Z";
  d += " M15 20.5 L17.5 23 L15 25.5 L12.5 23 Z M7 12.5 L9.5 15 L7 17.5 L4.5 15 Z";
  [5, 10, 15, 20, 25].forEach((x) => { d += " " + circ(x, 28.5, 1.2); });
  [4, 26].forEach((x) => { d += ` M${x - 1.6} 3 L${x + 1.6} 3 L${x} 5.6 Z`; });
  return d;
})();

export function PapelPicado({ className = "", color = C.rosa }) {
  return (
    <svg viewBox="0 0 30 36" className={className}>
      <path d={BANDERA_D} fill={color} fillRule="evenodd" fillOpacity="0.92" />
    </svg>
  );
}

// Cempasúchil de verdad (pedido del dueño, 2026-11-01: "mejora la animación
// de cempasúchil"): pétalos con la orilla rizada en 5 capas, del dorado
// oscuro de afuera al amarillo claro del centro, con una sombrita entre capa
// y capa para que se vea esponjosa. Al aparecer se ABRE (de adentro hacia
// afuera), luego se mece y cada capa se mueve a su propio ritmo, como con el
// aire. ligera = solo 3 capas y una sola animación (tarjetas de producto:
// salen muchas y en el celular pesaba).
const petaloD = (largo, ancho) => {
  const w = ancho / 2;
  // base angosta, se abre y termina en tres festones (la orilla rizada)
  return `M0 0 C${-w * 0.5} ${-largo * 0.25} ${-w * 1.05} ${-largo * 0.6} ${-w} ${-largo * 0.86}`
    + ` q${w * 0.3} ${-largo * 0.16} ${w * 0.66} ${-largo * 0.1}`
    + ` q${w * 0.34} ${-largo * 0.12} ${w * 0.68} ${-largo * 0.01}`
    + ` q${w * 0.34} ${-largo * 0.11} ${w * 0.66} ${largo * 0.11}`
    + ` C${w * 1.05} ${-largo * 0.6} ${w * 0.5} ${-largo * 0.25} 0 0 Z`;
};
// [pétalos, largo, ancho, color, giro inicial (grados)] de afuera hacia adentro
const CAPAS = [
  [16, 18, 7.2, C.oro1, 0],
  [14, 15, 6.6, C.oro2, 12],
  [12, 11.6, 6, C.oro3, 4],
  [9, 8.2, 5.2, C.oro4, 20],
  [6, 5, 4, C.oro5, 8],
];
const CAPAS_LIGERA = [CAPAS[0], CAPAS[2], CAPAS[4]];

export function Cempasuchil({ className = "", animada = true, ligera = false }) {
  const capas = ligera ? CAPAS_LIGERA : CAPAS;
  return (
    <svg viewBox="-21 -21 42 42" className={`overflow-visible ${className}`}>
      {animada && !ligera && <circle r="20" fill={C.oro4} opacity="0.2" className="mu-brillo-flor" />}
      <g className={animada ? (ligera ? "mu-flor" : "mu-flor mu-abre") : ""}>
        {capas.map(([n, largo, ancho, color, giro], k) => (
          <g key={k}>
            {/* sombrita de la capa de afuera sobre la de adentro: da volumen */}
            {k > 0 && <circle r={largo * 0.95} fill={C.oroBorde} opacity="0.22" />}
            <g className={animada && !ligera ? `mu-capa mu-capa-${k}` : ""}>
              {Array.from({ length: n }).map((_, i) => (
                <path key={i} d={petaloD(largo, ancho)} transform={`rotate(${giro + (i * 360) / n})`}
                  fill={color} stroke={C.oroBorde} strokeWidth="0.45" strokeOpacity="0.55" strokeLinejoin="round" />
              ))}
            </g>
          </g>
        ))}
        {/* corazón: granitos y un brillo */}
        <circle r="2.1" fill={C.oro2} />
        {[0, 72, 144, 216, 288].map((a) => (
          <circle key={a} cx={(Math.cos((a * Math.PI) / 180) * 1.1).toFixed(2)} cy={(Math.sin((a * Math.PI) / 180) * 1.1).toFixed(2)} r="0.45" fill={C.oroBorde} opacity="0.7" />
        ))}
        <ellipse cx="-4" cy="-6" rx="3.2" ry="1.8" fill="#fff" opacity="0.28" transform="rotate(-35 -4 -6)" />
      </g>
      {/* destellos que aparecen de vez en cuando */}
      {animada && !ligera && (
        <g fill="#fff">
          <path className="mu-destello" d="M15 -15 l0.8 -2.6 l0.8 2.6 l2.6 0.8 l-2.6 0.8 l-0.8 2.6 l-0.8 -2.6 l-2.6 -0.8 Z" />
          <path className="mu-destello mu-destello-2" d="M-16 10 l0.6 -2 l0.6 2 l2 0.6 l-2 0.6 l-0.6 2 l-0.6 -2 l-2 -0.6 Z" />
        </g>
      )}
    </svg>
  );
}

// Pétalo suelto que cae (misma forma rizada que la flor).
function PetaloSuelto({ tam, color }) {
  return (
    <svg viewBox="-5 -11 10 12" style={{ width: tam, height: tam * 1.2 }} className="block">
      <path d={petaloD(10, 7.5)} fill={color} stroke={C.oroBorde} strokeWidth="0.4" strokeOpacity="0.5" />
      <path d="M0 -1 L0 -7.5" stroke={C.oroBorde} strokeWidth="0.35" opacity="0.4" />
    </svg>
  );
}

export function Calaverita({ className = "", ojos = C.turquesa }) {
  return (
    <svg viewBox="0 0 60 62" className={className}>
      <path d="M30 4 C14 4 6 16 6 28 C6 38 12 44 16 46 L16 54 Q16 58 20 58 L40 58 Q44 58 44 54 L44 46 C48 44 54 38 54 28 C54 16 46 4 30 4 Z"
        fill={C.hueso} stroke="#e7e5e4" strokeWidth="1.2" />
      {/* flor en la frente */}
      <g fill={C.rosa}>
        <circle cx="30" cy="11.5" r="2.4" /><circle cx="33.4" cy="14.5" r="2.4" /><circle cx="32" cy="18.4" r="2.4" />
        <circle cx="28" cy="18.4" r="2.4" /><circle cx="26.6" cy="14.5" r="2.4" />
      </g>
      <circle cx="30" cy="15.4" r="1.8" fill={C.amarillo} />
      {/* ojos de flor */}
      {[21, 39].map((x) => (
        <g key={x}>
          <circle cx={x} cy="30" r="7.4" fill={ojos} />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <circle key={i} cx={(x + Math.cos(a) * 7.6).toFixed(2)} cy={(30 + Math.sin(a) * 7.6).toFixed(2)} r="1.5" fill={ojos} />;
          })}
          <circle cx={x} cy="30" r="3.8" fill={C.oscuro} />
          <circle cx={x + 1.2} cy="28.7" r="1.1" fill="#fff" />
        </g>
      ))}
      {/* nariz de corazón */}
      <path d="M30 44 L27.2 40.6 Q25.8 37.4 28.4 37.2 Q30 37.2 30 39 Q30 37.2 31.6 37.2 Q34.2 37.4 32.8 40.6 Z" fill={C.oscuro} />
      {/* cachetes */}
      <g fill={C.naranja}><circle cx="11" cy="37" r="1.4" /><circle cx="14" cy="40" r="1.1" /><circle cx="49" cy="37" r="1.4" /><circle cx="46" cy="40" r="1.1" /></g>
      {/* dientes cosidos */}
      <path d="M19 50.5 H41 M22.5 47.5 V53.5 M26.2 47.5 V53.5 M30 47.5 V53.5 M33.8 47.5 V53.5 M37.5 47.5 V53.5"
        stroke={C.oscuro} strokeWidth="1.3" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export function Vela({ className = "", color = C.hueso }) {
  return (
    <svg viewBox="0 0 20 50" className={`overflow-visible ${className}`}>
      <ellipse cx="10" cy="10" rx="9" ry="10" fill={C.amarillo} opacity="0.22" className="mu-halo" />
      <g className="mu-llama">
        <path d="M10 2 Q15 9 13 13 Q12 16 10 16 Q8 16 7 13 Q5 9 10 2 Z" fill={C.ambar} />
        <path d="M10 7 Q12.5 11 11.6 13.4 Q11 14.6 10 14.6 Q9 14.6 8.4 13.4 Q7.5 11 10 7 Z" fill={C.amarilloClaro} />
      </g>
      <path d="M10 16 V19" stroke={C.oscuro} strokeWidth="1" />
      <rect x="4" y="19" width="12" height="29" rx="2" fill={color} />
      <path d="M6 19 Q6 24 7.5 25 Q8.5 24 9 19 Z" fill="#fff" opacity="0.7" />
      <rect x="3" y="46" width="14" height="3" rx="1.5" fill="#a16207" />
    </svg>
  );
}

function Monarca({ className = "" }) {
  return (
    <svg viewBox="0 0 40 30" className={className}>
      {/* cada lado aletea desde el cuerpo, como bisagra */}
      <g className="mu-ala mu-ala-izq" stroke="#1c1917" strokeWidth="1.4" strokeLinejoin="round">
        <path d="M19 13 C14 2 4 0 2 5 C1 10 8 15 19 15 Z" fill={C.naranja} />
        <path d="M19 16 C10 16 5 22 9 26 C13 29 18 23 19 17 Z" fill={C.ambar} />
        <path d="M17 13 Q10 7 4 6 M17 17 Q12 21 9 24" strokeWidth="0.6" fill="none" />
        <g fill="#fff" stroke="none"><circle cx="4" cy="5.5" r="0.9" /><circle cx="6.5" cy="3.4" r="0.6" /><circle cx="9" cy="24" r="0.8" /></g>
      </g>
      <g className="mu-ala mu-ala-der" stroke="#1c1917" strokeWidth="1.4" strokeLinejoin="round">
        <path d="M21 13 C26 2 36 0 38 5 C39 10 32 15 21 15 Z" fill={C.naranja} />
        <path d="M21 16 C30 16 35 22 31 26 C27 29 22 23 21 17 Z" fill={C.ambar} />
        <path d="M23 13 Q30 7 36 6 M23 17 Q28 21 31 24" strokeWidth="0.6" fill="none" />
        <g fill="#fff" stroke="none"><circle cx="36" cy="5.5" r="0.9" /><circle cx="33.5" cy="3.4" r="0.6" /><circle cx="31" cy="24" r="0.8" /></g>
      </g>
      <ellipse cx="20" cy="15.5" rx="1.5" ry="7" fill="#1c1917" />
      <path d="M19.5 9 Q17 5 15.5 4 M20.5 9 Q23 5 24.5 4" stroke="#1c1917" strokeWidth="0.8" fill="none" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Papel picado bajo la barra superior                                 */
/* ------------------------------------------------------------------ */

function TramoPapel({ i }) {
  return (
    <div className="relative shrink-0 w-[34px] h-full">
      <svg viewBox="0 0 34 8" className="absolute inset-x-0 top-0 w-full h-[8px]" preserveAspectRatio="none">
        <path d="M0 1 Q17 5 34 1" stroke="#44403c" strokeWidth="1" fill="none" />
      </svg>
      <div className="mu-ondea absolute left-1/2 -ml-[13px] top-[2px]" style={{ animationDelay: `${-((i * 0.41) % 3)}s` }}>
        <PapelPicado className="w-[26px] h-[31px]" color={PAPEL[i % PAPEL.length]} />
      </div>
    </div>
  );
}

export default function DiaMuertosDecor() {
  if (!esTemporadaMuertos()) return null;
  return (
    <div aria-hidden="true" data-testid="muertos-decor"
      className="pointer-events-none select-none absolute left-0 right-0 top-full h-[38px] overflow-hidden flex">
      {Array.from({ length: 80 }).map((_, i) => <TramoPapel key={i} i={i} />)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Capa para toda la web: pétalos que caen y mariposas monarca         */
/* ------------------------------------------------------------------ */

// [posición, tamaño px, duración s, retraso s, solo pantallas grandes]
const PETALOS = [
  ["4%", 11, 14, 0, false], ["12%", 8, 18, -6, true], ["21%", 13, 16, -11, false],
  ["30%", 9, 20, -3, true], ["41%", 12, 15, -8, true], ["50%", 10, 19, -14, false],
  ["59%", 8, 17, -2, true], ["68%", 13, 21, -9, true], ["77%", 9, 16, -5, false],
  ["86%", 12, 18, -12, true], ["94%", 10, 15, -7, false], ["36%", 8, 22, -17, false],
];
const COLOR_PETALO = [C.oro2, C.oro3, C.oro1, C.oro4];

export function DiaMuertosOverlay() {
  if (!esTemporadaMuertos()) return null;
  return (
    <div aria-hidden="true" data-testid="muertos-overlay"
      className="pointer-events-none select-none fixed inset-0 z-[25] overflow-hidden">
      {PETALOS.map(([left, tam, dur, delay, soloGrande], i) => (
        <div key={i} className={`mu-cae absolute top-0 ${soloGrande ? "hidden sm:block" : ""}`}
          style={{ left, animationDuration: `${dur}s`, animationDelay: `${delay}s` }}>
          <div className="mu-vaiven" style={{ animationDelay: `${delay / 3}s`, animationDuration: `${2.8 + (i % 4) * 0.6}s` }}>
            {/* gira en 3D: se ve de frente y de canto, como pétalo de verdad */}
            <span className="mu-gira block" style={{ opacity: 0.92, animationDuration: `${1.8 + (i % 5) * 0.5}s` }}>
              <PetaloSuelto tam={Math.round(tam * 1.5)} color={COLOR_PETALO[i % 4]} />
            </span>
          </div>
        </div>
      ))}
      {/* mariposas: cada una sigue su propia ruta curva, se inclina hacia
          donde va, aletea en ráfagas y planea un momento */}
      <div className="mu-ruta-a absolute left-0 top-0">
        <div className="mu-cuerpo"><Monarca className="w-9 h-7 sm:w-11 sm:h-8" /></div>
      </div>
      <div className="mu-ruta-b absolute left-0 top-0 hidden sm:block">
        <div className="mu-cuerpo" style={{ animationDelay: "-0.7s" }}><Monarca className="w-8 h-6" /></div>
      </div>
      <div className="mu-ruta-c absolute left-0 top-0">
        <div className="mu-cuerpo" style={{ animationDelay: "-1.4s" }}><Monarca className="w-7 h-5 sm:w-8 sm:h-6" /></div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ofrendita al final de cada página                                   */
/* ------------------------------------------------------------------ */

// Va al FINAL de la página, no fija: fija tapaba los precios y chocaría con
// los botones flotantes de la tienda (igual que las calabazas de Halloween).
export function DiaMuertosPie() {
  if (!esTemporadaMuertos()) return null;
  return (
    <div aria-hidden="true" data-testid="muertos-pie"
      className="pointer-events-none select-none flex items-end justify-between px-3 sm:px-8 pb-2">
      <div className="flex items-end gap-1 sm:gap-2">
        <Vela className="w-4 h-10 sm:w-5 sm:h-12" />
        <Calaverita className="w-11 h-11 sm:w-14 sm:h-14" />
        <Cempasuchil className="w-7 h-7 sm:w-9 sm:h-9" />
      </div>
      <div className="flex items-end gap-1 sm:gap-2">
        <Cempasuchil className="w-7 h-7 sm:w-9 sm:h-9" />
        <Calaverita className="w-11 h-11 sm:w-14 sm:h-14" ojos={C.rosa} />
        <Vela className="w-4 h-10 sm:w-5 sm:h-12" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Banner de temporada (portada y tienda)                              */
/* ------------------------------------------------------------------ */

export function DiaMuertosBanner({ className = "" }) {
  if (!esTemporadaMuertos()) return null;
  return (
    <div aria-hidden="true" className={`relative overflow-hidden rounded-2xl text-left ${className}`}
      style={{ background: `radial-gradient(120% 140% at 85% 0%, ${C.noche2} 0%, ${C.noche} 62%)` }}>
      <div className="absolute inset-0 mu-estrellas opacity-80" />
      {/* tira de papel picado arriba */}
      <div className="absolute inset-x-0 top-0 h-7 sm:h-9 flex overflow-hidden">
        {Array.from({ length: 64 }).map((_, i) => (
          <PapelPicado key={i} className="shrink-0 w-6 h-7 sm:w-7 sm:h-9 -mt-1" color={PAPEL[i % PAPEL.length]} />
        ))}
      </div>
      <div className="relative px-5 sm:px-8 pt-10 sm:pt-12 pb-6 sm:pb-7 pr-32 sm:pr-56">
        <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]" style={{ color: C.amarilloClaro }}>Para nuestros revendedores</p>
        <p className="font-head text-2xl sm:text-4xl font-extrabold text-white mt-1 leading-tight">
          Gracias por estar <span style={{ color: C.oro3 }}>siempre</span>
        </p>
        <p className="text-sm sm:text-base text-pink-100/90 mt-1.5 max-w-md">
          En estos días recordamos a quienes nos acompañan. Ustedes son parte de esta familia: gracias por confiar en nosotros en cada venta.
        </p>
      </div>
      <div className="absolute right-3 sm:right-8 bottom-2 flex items-end gap-1 sm:gap-2">
        <Vela className="w-4 h-10 sm:w-6 sm:h-14" />
        <Calaverita className="w-16 h-16 sm:w-24 sm:h-24 mu-flota" />
        <div className="hidden sm:flex flex-col items-center gap-1">
          <Cempasuchil className="w-8 h-8" />
          <Cempasuchil className="w-10 h-10" />
        </div>
        <Vela className="w-4 h-10 sm:w-6 sm:h-14" color="#fbcfe8" />
      </div>
    </div>
  );
}

// Florecita de cempasúchil en la esquina de cada tarjeta de producto.
export function DiaMuertosCardFlor() {
  if (!esTemporadaMuertos()) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute bottom-1 left-1 flex items-end">
      <Cempasuchil ligera className="w-7 h-7 sm:w-9 sm:h-9 drop-shadow" />
      <Cempasuchil ligera className="w-4 h-4 sm:w-5 sm:h-5 -ml-1.5 drop-shadow" />
    </div>
  );
}
